import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface DashboardData {
  conversations?: { active: number; total: number }
  documents?: { pending: number; approved: number; rejected: number }
  outreach?: { daily_sent: number; daily_limit: number }
  model_calls?: { today: number; budget_tokens: number }
}

interface Config {
  OUTREACH_MODE?: string
  WEBHOOK_ENABLED?: string
  /** Kill switch del agente. Ausente = responde; 'false' = pausado. */
  AGENT_AUTO_REPLY?: string
  SERVICE_WINDOW_ENABLED?: string
  SERVICE_WINDOW_START?: string
  SERVICE_WINDOW_END?: string
}

function GaugeBar({ value, max, color, label }: { value: number; max: number; color: string; label: string }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--c-ink-muted)', marginBottom: 5 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700 }}>{pct}% ({value.toLocaleString()} / {max.toLocaleString()})</span>
      </div>
      <div style={{ height: 8, borderRadius: 99, background: 'var(--c-surface-border)', overflow: 'hidden' }}>
        <div style={{
          height: '100%', borderRadius: 99, transition: 'width 0.4s ease',
          width: `${pct}%`, background: color,
        }} />
      </div>
      {pct >= 80 && (
        <div style={{ fontSize: 11, color: 'var(--c-danger)', marginTop: 4, fontWeight: 600 }}>
          ⚠ Cerca del límite — revisar uso.
        </div>
      )}
    </div>
  )
}

interface KillSwitchProps {
  label: string
  description: string
  active: boolean
  disabled: boolean
  loading: boolean
  onToggle: () => void
}

interface ServiceWindowCardProps {
  enabled: boolean
  start: string
  end: string
  isAdmin: boolean
  saving: string | null
  onToggle: () => void
  onSaveHours: (start: string, end: string) => void | Promise<void>
}

function ServiceWindowCard({ enabled, start, end, isAdmin, saving, onToggle, onSaveHours }: ServiceWindowCardProps) {
  const [editing, setEditing] = useState(false)
  const [draftStart, setDraftStart] = useState(start)
  const [draftEnd, setDraftEnd] = useState(end)

  useEffect(() => { setDraftStart(start); setDraftEnd(end) }, [start, end])

  return (
    <div style={{
      padding: '14px 0', borderBottom: '1px solid var(--c-surface-faint)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-ink)' }}>
            Horario de atención del bot
          </div>
          <div style={{ fontSize: 12, color: 'var(--c-ink-muted)', marginTop: 2 }}>
            {enabled
              ? `Activo: ${start}–${end} (Lima). Fuera de este horario el bot responde con el mensaje de fuera de servicio.`
              : 'Desactivado: el bot atiende 24/7. Útil para pruebas.'}
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={!isAdmin || saving === 'SERVICE_WINDOW_ENABLED'}
          style={{
            padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, flexShrink: 0,
            border: 'none', cursor: !isAdmin || saving === 'SERVICE_WINDOW_ENABLED' ? 'not-allowed' : 'pointer',
            background: enabled ? 'var(--c-danger-light)' : 'var(--c-brand-light)',
            color: enabled ? 'var(--c-danger-ink)' : 'var(--c-brand-ink)',
            opacity: saving === 'SERVICE_WINDOW_ENABLED' ? 0.6 : 1,
          }}
        >
          {saving === 'SERVICE_WINDOW_ENABLED' ? '…' : enabled ? 'Desactivar (24/7)' : 'Activar horario'}
        </button>
      </div>

      {enabled && (
        <div style={{
          background: 'var(--c-surface-subtle)', borderRadius: 8, padding: '10px 14px',
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          {!editing ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--c-ink-muted)' }}>
                <strong style={{ color: 'var(--c-ink)' }}>Rango actual:</strong> {start} a {end} (Lima, GMT-5)
              </div>
              {isAdmin && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    all: 'unset', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    color: 'var(--c-info-ink)', padding: '3px 10px',
                    border: '1px solid var(--c-info-border)', borderRadius: 6,
                  }}
                >
                  Cambiar horario
                </button>
              )}
            </>
          ) : (
            <>
              <label style={{ fontSize: 12, color: 'var(--c-ink-muted)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                Desde
                <input type="time" value={draftStart} onChange={e => setDraftStart(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-surface-strong)', fontSize: 13 }} />
              </label>
              <label style={{ fontSize: 12, color: 'var(--c-ink-muted)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                Hasta
                <input type="time" value={draftEnd} onChange={e => setDraftEnd(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--c-surface-strong)', fontSize: 13 }} />
              </label>
              <div style={{ display: 'flex', gap: 6, marginTop: 15 }}>
                <button
                  onClick={async () => {
                    await onSaveHours(draftStart, draftEnd)
                    setEditing(false)
                  }}
                  disabled={saving === 'SERVICE_WINDOW_HOURS' || draftStart >= draftEnd}
                  style={{
                    all: 'unset', cursor: saving === 'SERVICE_WINDOW_HOURS' || draftStart >= draftEnd ? 'not-allowed' : 'pointer',
                    padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                    background: 'var(--c-brand)', color: 'white',
                    opacity: saving === 'SERVICE_WINDOW_HOURS' || draftStart >= draftEnd ? 0.5 : 1,
                  }}
                >
                  {saving === 'SERVICE_WINDOW_HOURS' ? 'Guardando…' : 'Guardar'}
                </button>
                <button
                  onClick={() => { setDraftStart(start); setDraftEnd(end); setEditing(false) }}
                  style={{
                    all: 'unset', cursor: 'pointer',
                    padding: '6px 12px', borderRadius: 6, fontSize: 12,
                    border: '1px solid var(--c-surface-strong)', color: 'var(--c-ink-soft)',
                  }}
                >
                  Cancelar
                </button>
              </div>
              {draftStart >= draftEnd && (
                <div style={{ fontSize: 11, color: 'var(--c-danger)', width: '100%', marginTop: 4 }}>
                  La hora de fin debe ser posterior a la de inicio.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function KillSwitch({ label, description, active, disabled, loading, onToggle }: KillSwitchProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid var(--c-surface-faint)', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-ink)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--c-ink-muted)', marginTop: 2 }}>{description}</div>
      </div>
      <button
        onClick={onToggle}
        disabled={disabled || loading}
        style={{
          padding: '7px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, flexShrink: 0,
          border: 'none', cursor: disabled || loading ? 'not-allowed' : 'pointer',
          background: active ? 'var(--c-danger-light)' : 'var(--c-brand-light)',
          color: active ? 'var(--c-danger-ink)' : 'var(--c-brand-ink)',
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? '…' : active ? 'Pausar' : 'Reanudar'}
      </button>
    </div>
  )
}

export default function Costs() {
  const { isAdmin } = useAuth()
  const [dashboard, setDashboard] = useState<DashboardData | null>(null)
  const [config, setConfig]       = useState<Config>({})
  const [loading, setLoading]     = useState(true)
  const [saving, setSaving]       = useState<string | null>(null)
  const [error, setError]         = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [dashRes, cfgRes] = await Promise.all([
        apiClient.get<DashboardData>('/dashboard'),
        apiClient.get<Config>('/config'),
      ])
      setDashboard(dashRes.data)
      setConfig(cfgRes.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchAll() }, [fetchAll])

  const toggle = async (key: keyof Config, activeVal: string, inactiveVal: string) => {
    if (!isAdmin) return
    setSaving(key)
    try {
      const current = config[key]
      const newVal  = current === activeVal ? inactiveVal : activeVal
      await apiClient.put('/config', { [key]: newVal })
      setConfig(prev => ({ ...prev, [key]: newVal }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar')
    } finally {
      setSaving(null)
    }
  }

  if (loading) return <LoadingSpinner label="Cargando métricas de costos…" />

  const tokenToday  = dashboard?.model_calls?.today ?? 0
  const tokenBudget = dashboard?.model_calls?.budget_tokens ?? 1_000_000
  const dailySent   = dashboard?.outreach?.daily_sent ?? 0
  const dailyLimit  = dashboard?.outreach?.daily_limit ?? 50

  const outreachActive = config.OUTREACH_MODE !== 'DISABLED'
  const webhookActive  = config.WEBHOOK_ENABLED === 'true'
  // Sin la clave escrita, el agente responde. El default es "encendido" a
  // propósito: un kill switch que se activa por omisión dejaría al bot mudo en
  // un despliegue nuevo sin que nadie lo haya pedido.
  const agenteActivo   = config.AGENT_AUTO_REPLY !== 'false'
  // Por defecto SÍ hay horario. Se desactiva ponendo SERVICE_WINDOW_ENABLED = 'false' (bot 24/7).
  const serviceWindowActive = config.SERVICE_WINDOW_ENABLED !== 'false'

  const TOKEN_COST_PER_1K = 0.00027  // Gemma 3 12B en Bedrock — referencial

  return (
    <div>

      {error && <ErrorAlert message={error} />}

      {/* Stats superiores */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Tokens hoy', value: tokenToday.toLocaleString(), sub: `~USD ${((tokenToday / 1000) * TOKEN_COST_PER_1K).toFixed(4)}` },
          { label: 'Outreach hoy', value: `${dailySent} / ${dailyLimit}`, sub: dailySent >= dailyLimit * 0.8 ? '⚠ Cerca del límite' : 'dentro del límite' },
          { label: 'Presupuesto TEST', value: 'USD 30', sub: 'mensual de referencia' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', border: '1px solid var(--c-surface-border)',
            borderRadius: 12, padding: '18px 22px', flex: 1, minWidth: 140,
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--c-ink)' }}>{s.value}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-ink-soft)', marginTop: 2 }}>{s.label}</div>
            <div style={{ fontSize: 11, color: 'var(--c-ink-faint)', marginTop: 3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Barras de cuota */}
      <div style={{
        background: '#fff', border: '1px solid var(--c-surface-border)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 16,
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)' }}>Cuotas en tiempo real</div>
        <GaugeBar
          value={tokenToday} max={tokenBudget}
          color="var(--c-info)"
          label="Tokens del modelo (Gemma 3 / Bedrock)"
        />
        <GaugeBar
          value={dailySent} max={dailyLimit}
          color="var(--c-brand)"
          label="Contactos proactivos diarios"
        />
      </div>

      {/* Kill switches */}
      <div style={{
        background: '#fff', border: '1px solid var(--c-surface-border)',
        borderRadius: 12, padding: '20px 24px',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-ink)', marginBottom: 4 }}>Kill switches</div>
        <div style={{ fontSize: 13, color: 'var(--c-ink-muted)', marginBottom: 14 }}>
          Desactivan un componente sin redespliegue. Solo Administradores.
        </div>

        {!isAdmin && (
          <div style={{
            padding: '10px 14px', borderRadius: 9, marginBottom: 14,
            background: 'var(--c-info-tint)', border: '1px solid var(--c-info-border)',
            fontSize: 13, color: 'var(--c-info-ink)',
          }}>
            Requiere rol Administrador para activar o desactivar.
          </div>
        )}

        <ServiceWindowCard
          enabled={serviceWindowActive}
          start={config.SERVICE_WINDOW_START ?? '08:00'}
          end={config.SERVICE_WINDOW_END ?? '20:00'}
          isAdmin={isAdmin}
          saving={saving}
          onToggle={() => void toggle('SERVICE_WINDOW_ENABLED', 'true', 'false')}
          onSaveHours={async (s, e) => {
            if (!isAdmin) return
            setSaving('SERVICE_WINDOW_HOURS')
            try {
              await apiClient.put('/config', { SERVICE_WINDOW_START: s, SERVICE_WINDOW_END: e })
              setConfig(prev => ({ ...prev, SERVICE_WINDOW_START: s, SERVICE_WINDOW_END: e }))
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Error al guardar horario')
            } finally { setSaving(null) }
          }}
        />
        <KillSwitch
          label="Contacto proactivo (Outreach)"
          description="Pausa todos los envíos manuales de WhatsApp; conversaciones entrantes siguen funcionando."
          active={outreachActive}
          disabled={!isAdmin}
          loading={saving === 'OUTREACH_MODE'}
          onToggle={() => void toggle('OUTREACH_MODE', 'MANUAL_ALLOWLIST', 'DISABLED')}
        />
        <KillSwitch
          label="Respuestas automáticas del agente"
          description="Pausa al agente IA en el acto. Las conversaciones que entren pasan a un asesor por round-robin: no quedan sin atender."
          active={agenteActivo}
          disabled={!isAdmin}
          loading={saving === 'AGENT_AUTO_REPLY'}
          onToggle={() => void toggle('AGENT_AUTO_REPLY', 'true', 'false')}
        />
        <KillSwitch
          label="Webhook n8n"
          description="Deja de recibir eventos de HubSpot reenviados por n8n."
          active={webhookActive}
          disabled={!isAdmin}
          loading={saving === 'WEBHOOK_ENABLED'}
          onToggle={() => void toggle('WEBHOOK_ENABLED', 'true', 'false')}
        />

        {/* Estos dos no son kill switches pendientes de cablear: apagarían
            funciones que el agente todavía NO tiene. Se listan para que quede
            claro qué no hace el agente hoy, con el motivo real en vez de un
            "Próxima fase" que sugiere que solo falta conectarlos. */}
        {[
          { label: 'Consultas a HubSpot',
            desc: 'El agente no consulta HubSpot en vivo: usa el nombre y el id que el import dejó en la conversación. No hay llamada que apagar.' },
          { label: 'Base de conocimiento',
            desc: 'El agente todavía no consulta la base al responder (kb_fragments está vacío en conversation/handler.py). No hay consulta que apagar.' },
        ].map(ks => (
          <div key={ks.label} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 0', borderBottom: '1px solid var(--c-surface-faint)', gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-ink-muted)' }}>{ks.label}</div>
              <div style={{ fontSize: 12, color: 'var(--c-ink-faint)', marginTop: 2 }}>{ks.desc}</div>
            </div>
            <span style={{
              padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              background: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)',
            }}>
              Sin función
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
